// Host contract: route matches `/admin/integrations/<kind>` (or sets
// `root._genericIntKindOverride`); `root._integrationHub` populated by
// `loadIntegrationCatalogue`; `root._genericIntFormOpen` toggles the form.
import {
  saveIntegration,
  deleteIntegration,
  callCapability,
  reorderIntegrations
} from '../functions/integrations/index.js'
import { slugify } from '../functions/slugify.js'

// ── New-instance form ────────────────────────────────────────────────

export const GenericIntNewForm = {
  // FA204 / frank trap — every helper below lives in `scope`, not module
  // scope: a bare module-scope const/function silently reads as undefined
  // once this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Called via
  // `el.scope.<name>(...)` from any descendant factory/handler. Regular
  // method syntax (not arrows) throughout — `el.scope.foo()` binds `this`
  // to `el.scope`, so a sibling helper calling another does it via
  // `this.<name>(...)` (object-literal properties can't see each other as
  // bare identifiers otherwise). `kindRow`/`kindFromRoot`/`toast`/`DRAFT_KEY`
  // are ALSO needed by the sibling `GenericIntInstanceCard`/
  // `GenericIntConfigField` exports below — `el.scope` only resolves within
  // the tree of the specific top-level export it's defined on, so they're
  // duplicated there rather than shared.
  scope: {
    DRAFT_KEY: '_genericIntDraft',

    kindFromRoot(root) {
      const override = root?._genericIntKindOverride
      if (typeof override === 'string' && override) return override
      const m = String(root?.currentPath || '').match(/^\/admin\/integrations\/([^?#/]+)/)
      return m ? m[1] : null
    },

    kindRow(root) {
      const kind = this.kindFromRoot(root)
      if (!kind) return null
      return (root?._integrationHub?.kinds || []).find((k) => k.kind === kind) || null
    },

    // ui_flow hint column (migration 0074) — tells the form which variant
    // UX to render. Today the column is populated only for registrar rows
    // (backfill) but no registrar rows exist in `integration_kinds` yet,
    // so every form instance evaluates to 'credential'. Wiring lives
    // here so MARKETPLACE's future `MarketplaceRegistrarDomainPicker` +
    // the OAuth2 connect button can drop in without touching save/delete
    // logic. See NEEDED_FOR_MARKETPLACE.md § REQUEST tick-3.
    uiFlow(root) {
      const kr = this.kindRow(root)
      return kr?.ui_flow || 'credential'
    },

    // Toast helper — openNotification depends on editor-app internals and
    // throws when GlobalNotification isn't fully wired (e.g. on the workspace app).
    // Swallowing the throw keeps the save/delete flow intact even if the
    // toast infrastructure is missing; fall back to console for diagnostics.
    toast(el, payload) {
      try {
        el.call('openNotification', payload)
      } catch (err) {
        console.warn('[integration]', payload.title, payload.message || '', err)
      }
    },

    async saveNew(el, s) {
      const orgId = s.root?.jwtClaims?.workspace_org_id
      if (!orgId) {
        this.toast(el, {
          title: el.call('polyglot', 'apps.integration.noWorkspace'),
          type: 'error'
        })
        return
      }
      const kind = this.kindFromRoot(s.root)
      const kindRow = this.kindRow(s.root)
      const draft = s.root?.[this.DRAFT_KEY] || {}
      const fields = kindRow?.config_schema?.fields || []

      for (const f of fields) {
        if (f.required && !draft[f.key]) {
          this.toast(el, {
            title: el.call('polyglot', 'apps.integration.missingField.title'),
            message: el
              .call('polyglot', 'apps.integration.missingField.message')
              .replace('{field}', el.call('polyglot', f.label)),
            type: 'error'
          })
          return
        }
      }

      const config = {}
      for (const f of fields) {
        if (draft[f.key] != null && draft[f.key] !== '') config[f.key] = draft[f.key]
      }
      const displayName = draft.displayName || config.display_name || kind

      const slug = kindRow?.allows_multiple
        ? slugify(draft.slug || draft.displayName || kind)
        : 'default'

      const { scopeType, scopeId } = el.call('resolveScope', draft, kindRow?.default_scope || 'org')
      if ((scopeType === 'workspace' || scopeType === 'project') && !scopeId) {
        this.toast(el, {
          title: el.call('polyglot', 'apps.integration.scopeRequired.title'),
          message: el
            .call('polyglot', 'apps.integration.scopeRequired.message')
            .replace('{scope}', scopeType),
          type: 'error'
        })
        return
      }

      const { ok, body } = await saveIntegration({
        orgId,
        kind,
        slug,
        displayName,
        config,
        secret: draft.secret || undefined,
        scopeType,
        scopeId,
        enabled: true
      })

      if (ok) {
        s.root.update({ [this.DRAFT_KEY]: null, _genericIntFormOpen: false })
        // Reload the catalogue so the new instance appears in the list.
        // Host page can bolt on a `loadCatalogue` function; try el.call so
        // the form doesn't hard-couple to a specific loader name.
        try {
          await el.call('reloadIntegrationHub')
        } catch {}
        this.toast(el, {
          title: el.call('polyglot', 'apps.integration.connected').replace('{name}', displayName),
          type: 'success'
        })
      } else {
        this.toast(el, {
          title: el.call('polyglot', 'apps.integration.connectFailed'),
          message: body?.error || el.call('polyglot', 'apps.integration.unknownError'),
          type: 'error'
        })
      }
    }
  },
  hide: (el, s) => !s.root?._genericIntFormOpen,
  flow: 'y',
  gap: 'Z1',
  padding: 'A',
  round: 'radiusControl',
  // Fill-contrast card (was a `line` border — border-habit sweep,
  // design-direction.md §8) — the form's own `overlay` fill already
  // separates it from its container.
  background: 'overlay',

  FormTitle: {
    tag: 'h3',
    fontSize: 'A',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    // `{kind}` is the integration-kind record's own display name (server
    // content), interpolated into a translated frame rather than concatenated.
    text: (el, s) => {
      const kr = el.scope.kindRow(s.root)
      return kr?.label
        ? el.call('polyglot', 'apps.integration.connectKind').replace('{kind}', kr.label)
        : el.call('polyglot', 'apps.integration.connect')
    }
  },

  DisplayName: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => !el.scope.kindRow(s.root)?.allows_multiple,
    Label: {
      tag: 'label',
      fontSize: 'Y2',
      color: 'caption',
      fontWeight: '700',
      text: '{{ apps.integration.displayName | polyglot }}'
    },
    Input: {
      tag: 'input',
      type: 'text',
      width: '100%',
      theme: 'field',
      minHeight: 'controlH',
      round: 'radiusControl',
      color: 'title',
      fontSize: 'Z2',
      padding: '0 A',
      placeholder: (el, s) =>
        el.scope.kindRow(s.root)?.label ||
        el.call('polyglot', 'apps.integration.displayNamePlaceholder'),
      value: (el, s) => s.root?.[el.scope.DRAFT_KEY]?.displayName || '',
      onInput: (e, el, s) => {
        s.root.update({
          [el.scope.DRAFT_KEY]: {
            ...(s.root?.[el.scope.DRAFT_KEY] || {}),
            displayName: e.target.value
          }
        })
      }
    }
  },

  ConfigFields: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'GenericIntConfigField',
    childrenAs: 'state',
    children: (el, s) => {
      const fields = el.scope.kindRow(s.root)?.config_schema?.fields || []
      return fields.filter((f) => f.key !== 'display_name')
    }
  },

  IntegrationScopePicker: {
    // Plain literal, not a factory — must match `scope.DRAFT_KEY` above
    // (kept in sync manually; both are the same '_genericIntDraft' data
    // constant, duplicated because this is a direct prop assignment
    // evaluated at declaration time, not inside a re-eval'd factory).
    draftKey: '_genericIntDraft',
    defaultScopeType: (el, s) => el.scope.kindRow(s.root)?.default_scope || 'org'
  },

  SecretField: {
    flow: 'y',
    gap: 'V',
    // Hide when:
    //   - no kind row yet,
    //   - auth_model='none' (no credential needed),
    //   - ui_flow='oauth2' (user connects via provider dialog, not a typed secret),
    //   - ui_flow='webhook' (URL is the credential, collected in ConfigFields).
    // Registrar + credential flows keep the field visible.
    hide: (el, s) => {
      const kr = el.scope.kindRow(s.root)
      if (!kr || kr.auth_model === 'none') return true
      const flow = el.scope.uiFlow(s.root)
      return flow === 'oauth2' || flow === 'webhook'
    },
    Label: {
      tag: 'label',
      fontSize: 'Y2',
      color: 'caption',
      fontWeight: '700',
      text: (el, s) =>
        el.scope.kindRow(s.root)?.secret_label ||
        el.call('polyglot', 'apps.integration.secretLabel')
    },
    Hint: {
      tag: 'p',
      fontSize: 'Y1',
      color: 'caption',
      margin: '0 0 V 0',
      hide: (el, s) => !el.scope.kindRow(s.root)?.secret_hint,
      text: (el, s) => el.scope.kindRow(s.root)?.secret_hint || ''
    },
    Input: {
      tag: 'input',
      type: 'password',
      width: '100%',
      theme: 'field',
      minHeight: 'controlH',
      round: 'radiusControl',
      color: 'title',
      fontSize: 'Z2',
      padding: '0 A',
      placeholder: '••••••••••••••••',
      value: (el, s) => s.root?.[el.scope.DRAFT_KEY]?.secret || '',
      onInput: (e, el, s) => {
        s.root.update({
          [el.scope.DRAFT_KEY]: { ...(s.root?.[el.scope.DRAFT_KEY] || {}), secret: e.target.value }
        })
      }
    }
  },

  // OAuth2 flow replaces the secret field — user connects via provider
  // dialog instead of typing a key. `startOAuthFlow` is a registered
  // function in `workspace/packages/workspace/functions/` (INTEGRATIONS
  // scope). Today it's a stub; wired fully when DOMAIN's Cloudflare
  // registrar / other OAuth2 kinds land. MARKETPLACE tick 4 spec.
  MarketplaceOAuthConnectButton: {
    if: (el, s) => el.scope.uiFlow(s.root) === 'oauth2'
  },

  // Registrar flow adds a domain management card AFTER the secret
  // field — user enters API key first, then uses the picker to search /
  // register domains via `sdk-bridge.callCapability`. v0 is a hint
  // card; MARKETPLACE replaces with search+results UI once DOMAIN's
  // first provider handler (porkbun.ts — shipped 2026-04-24) is wired
  // into `integration-call`'s dispatch table. MARKETPLACE tick 4 spec.
  MarketplaceRegistrarDomainPicker: {
    if: (el, s) => el.scope.uiFlow(s.root) === 'registrar'
  },

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center flex-end',
    marginTop: 'Z',

    CancelBtn: {
      tag: 'button',
      type: 'button',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      // Ghost fill (was transparent + `line` outline — the outlined-button
      // habit, border-habit sweep). Rest = overlay, hover = the house hover
      // wash.
      background: 'overlay',
      color: 'title',
      ':hover': { background: 'hover' },
      fontSize: 'Z2',
      cursor: 'pointer',
      text: '{{ common.action.cancel | polyglot }}',
      onClick: (e, el, s) => {
        s.root.update({ _genericIntFormOpen: false, [el.scope.DRAFT_KEY]: null })
      }
    },

    SaveBtn: {
      tag: 'button',
      type: 'button',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      // Reviewed (fixed-colour sweep): kept — fixed brand-accent CTA
      // (mirrors CancelBtn's sibling, which correctly uses adaptive `title`
      // on `overlay`; this button's own backing never adapts).
      background: 'blue',
      color: 'fillInk',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      border: 'none',
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root?._genericIntSaving ? 'apps.integration.connecting' : 'apps.integration.connect'
        ),
      onClick: async (e, el, s) => {
        s.root.update({ _genericIntSaving: true })
        await el.scope.saveNew(el, s)
        s.root.update({ _genericIntSaving: false })
      }
    }
  }
}

// ── Config field (driven by config_schema.fields) ────────────────────

export const GenericIntConfigField = {
  // FA204 / frank trap (see GenericIntNewForm above) — DRAFT_KEY duplicated
  // here since `el.scope` doesn't cross top-level export boundaries.
  scope: {
    DRAFT_KEY: '_genericIntDraft'
  },
  flow: 'y',
  gap: 'V',

  Label: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: (el, s) => {
      const req = s.required ? ' *' : ''
      return (s.label || s.key || '') + req
    }
  },

  Input: {
    tag: 'input',
    type: 'text',
    width: '100%',
    padding: 'Z',
    round: 'radiusControl',
    // Was a hand-rolled `background: 'document'` + `line` border — a real
    // bug found while sweeping (border-habit, design-direction.md §8): its
    // siblings in this same file (SecretField.Input, DisplayName.Input) both
    // correctly use the branded `field` theme, which already carries its own
    // state-managed border (§2.5 exception #4). This one had reinvented a
    // plain box instead of reusing it.
    theme: 'field',
    color: 'title',
    fontSize: 'Z2',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => s.root?.[el.scope.DRAFT_KEY]?.[s.key] || '',
    onInput: (e, el, s) => {
      s.root.update({
        [el.scope.DRAFT_KEY]: {
          ...(s.root?.[el.scope.DRAFT_KEY] || {}),
          [s.key]: e.target.value
        }
      })
    }
  }
}

// ── Instance card ────────────────────────────────────────────────────

export const GenericIntInstanceCard = {
  // FA204 / frank trap (see GenericIntNewForm above) — kindFromRoot/kindRow/
  // toast duplicated here since `el.scope` doesn't cross top-level export
  // boundaries.
  scope: {
    kindFromRoot(root) {
      const override = root?._genericIntKindOverride
      if (typeof override === 'string' && override) return override
      const m = String(root?.currentPath || '').match(/^\/admin\/integrations\/([^?#/]+)/)
      return m ? m[1] : null
    },

    kindRow(root) {
      const kind = this.kindFromRoot(root)
      if (!kind) return null
      return (root?._integrationHub?.kinds || []).find((k) => k.kind === kind) || null
    },

    // Toast helper — openNotification depends on editor-app internals and
    // throws when GlobalNotification isn't fully wired (e.g. on the workspace app).
    // Swallowing the throw keeps the save/delete flow intact even if the
    // toast infrastructure is missing; fall back to console for diagnostics.
    toast(el, payload) {
      try {
        el.call('openNotification', payload)
      } catch (err) {
        console.warn('[integration]', payload.title, payload.message || '', err)
      }
    },

    async deleteInstance(el, s, slug, scopeType, scopeId) {
      const orgId = s.root?.jwtClaims?.workspace_org_id
      if (!orgId) return
      const kind = this.kindFromRoot(s.root)
      const { ok, body } = await deleteIntegration({ orgId, kind, slug, scopeType, scopeId })
      if (ok) {
        try {
          await el.call('reloadIntegrationHub')
        } catch {}
        this.toast(el, {
          title: el.call('polyglot', 'apps.integration.disconnected'),
          type: 'success'
        })
      } else {
        this.toast(el, {
          title: el.call('polyglot', 'apps.integration.removeFailed'),
          message: body?.error || el.call('polyglot', 'apps.integration.serverError'),
          type: 'error'
        })
      }
    },

    // ── §I12 Instance reorder helpers ───────────────────────────────────
    //
    // HTML5 drag-and-drop reorder for GenericIntInstanceCard lists.
    // We store the drag source slug on root._genericIntDragSlug so the
    // dragover / drop handlers can read it regardless of which nested
    // element fires the event.
    //
    // On drop we:
    //   1. Reorder root._integrationHub.integrations in-memory (instant UI).
    //   2. Derive the new slug order for the affected (kind, scope) subset.
    //   3. Call reorderIntegrations() to persist positions server-side.

    instancesForKindScope(root) {
      const kind = this.kindFromRoot(root)
      if (!kind) return []
      return (root?._integrationHub?.integrations || []).filter((i) => i.kind === kind)
    },

    reorderInHub(root, kind, fromSlug, toSlug) {
      if (fromSlug === toSlug) return
      const all = root?._integrationHub?.integrations || []
      const kindItems = all.filter((i) => i.kind === kind)
      const others = all.filter((i) => i.kind !== kind)

      const fromIdx = kindItems.findIndex((i) => i.slug === fromSlug)
      const toIdx = kindItems.findIndex((i) => i.slug === toSlug)
      if (fromIdx < 0 || toIdx < 0) return

      const reordered = [...kindItems]
      const [moved] = reordered.splice(fromIdx, 1)
      reordered.splice(toIdx, 0, moved)

      root.update({
        _integrationHub: {
          ...root._integrationHub,
          integrations: [...others, ...reordered]
        },
        _genericIntDragSlug: null,
        _genericIntDragOverSlug: null
      })
    },

    async persistReorder(el, s) {
      const orgId = s.root?.jwtClaims?.workspace_org_id
      if (!orgId) return
      const kind = this.kindFromRoot(s.root)
      if (!kind) return
      const items = this.instancesForKindScope(s.root)
      if (items.length < 2) return
      const kindRow = this.kindRow(s.root)
      const scopeType = items[0]?.scope_type || kindRow?.default_scope || 'org'
      const scopeId = items[0]?.scope_id || null
      const slugs = items.map((i) => i.slug)
      const { ok, body } = await reorderIntegrations({ orgId, kind, slugs, scopeType, scopeId })
      if (!ok) {
        this.toast(el, {
          title: el.call('polyglot', 'apps.integration.reorderFailed'),
          message: body?.error || el.call('polyglot', 'apps.integration.unknownError'),
          type: 'error'
        })
      }
    }
  },
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'radiusControl',
  // Fill-contrast card (was a `line` border, redundant on top of this same
  // `overlay` fill — border-habit sweep, design-direction.md §8). The
  // functional drag-over indicator moves from a border-color swap to a fill
  // swap for the same reason (states use fill, not stroke, §4.3/§8.1) —
  // it stays a hue (not `selected`) because it signals a drop TARGET, a
  // distinct transient affordance, not a selection.
  background: (el, s) => (s.root?._genericIntDragOverSlug === s.slug ? 'blue.1' : 'overlay'),
  opacity: (el, s) => (s.root?._genericIntDragSlug === s.slug ? '0.4' : '1'),
  // §I12 — drag-and-drop reorder
  draggable: 'true',
  onDragStart: (e, el, s) => {
    s.root.update({ _genericIntDragSlug: s.slug })
    e.dataTransfer.effectAllowed = 'move'
  },
  onDragOver: (e, el, s) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const dragSlug = s.root?._genericIntDragSlug
    if (dragSlug && dragSlug !== s.slug) {
      s.root.update({ _genericIntDragOverSlug: s.slug })
    }
  },
  onDragLeave: (e, el, s) => {
    if (s.root?._genericIntDragOverSlug === s.slug) {
      s.root.update({ _genericIntDragOverSlug: null })
    }
  },
  onDrop: async (e, el, s) => {
    e.preventDefault()
    const fromSlug = s.root?._genericIntDragSlug
    const toSlug = s.slug
    if (!fromSlug || fromSlug === toSlug) {
      s.root.update({ _genericIntDragSlug: null, _genericIntDragOverSlug: null })
      return
    }
    const kind = el.scope.kindFromRoot(s.root)
    el.scope.reorderInHub(s.root, kind, fromSlug, toSlug)
    await el.scope.persistReorder(el, s)
  },
  onDragEnd: (e, el, s) => {
    s.root.update({ _genericIntDragSlug: null, _genericIntDragOverSlug: null })
  },

  Header: {
    flow: 'x',
    align: 'center space-between',

    Left: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      DragHandle: {
        tag: 'span',
        text: '⠿',
        color: 'caption',
        fontSize: 'A',
        cursor: 'grab',
        // Prevent text selection during drag
        userSelect: 'none'
      },
      Name: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.display_name || s.slug || ''
      },
      IntegrationScopeBadge: {}
    },

    Right: {
      flow: 'x',
      gap: 'Z',
      align: 'center',

      StatusPill: {
        padding: 'V Z',
        round: 'radiusPill',
        fontSize: 'Y1',
        fontWeight: '700',
        background: (el, s) => (s.enabled ? 'green' : 'overlayStrong'),
        color: (el, s) => (s.enabled ? 'fillInk' : 'caption'),
        text: (el, s) =>
          el.call(
            'polyglot',
            s.enabled ? 'apps.integration.status.live' : 'apps.integration.status.disabled'
          )
      },

      TestBtn: {
        tag: 'button',
        type: 'button',
        hide: (el, s) => {
          const caps = el.scope.kindRow(s.root)?.capabilities || []
          return caps.length === 0 || !s.enabled
        },
        minHeight: 'controlHCompact',
        padding: '0 Z',
        round: 'radiusPill',
        // Ghost fill (was transparent + `line` outline — the outlined-button
        // habit, border-habit sweep). Rest = overlay, hover = the house
        // hover wash.
        background: 'overlay',
        color: 'title',
        ':hover': { background: 'hover' },
        fontSize: 'Z',
        cursor: 'pointer',
        disabled: (el, s) => !!s.root?._genericIntTesting?.[s.slug],
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?._genericIntTesting?.[s.slug]
              ? 'apps.integration.testing'
              : 'apps.integration.test'
          ),
        onClick: async (e, el, s) => {
          const orgId = s.root?.jwtClaims?.workspace_org_id
          if (!orgId) return
          const caps = el.scope.kindRow(s.root)?.capabilities || []
          if (!caps.length) return
          s.root.update({
            _genericIntTesting: { ...(s.root?._genericIntTesting || {}), [s.slug]: true }
          })
          const res = await callCapability(
            { orgId, kind: el.scope.kindFromRoot(s.root), slug: s.slug },
            caps[0],
            {}
          )
          s.root.update({
            _genericIntTesting: { ...(s.root?._genericIntTesting || {}), [s.slug]: false }
          })
          el.scope.toast(
            el,
            res?.error
              ? {
                  title: el.call('polyglot', 'apps.integration.testFailed'),
                  message: res.error,
                  type: 'error'
                }
              : { title: el.call('polyglot', 'apps.integration.testPassed'), type: 'success' }
          )
        }
      },

      RemoveBtn: {
        tag: 'button',
        type: 'button',
        minHeight: 'controlHCompact',
        padding: '0 Z',
        round: 'radiusPill',
        // Ghost fill (was transparent + `line` outline — the outlined-button
        // habit, border-habit sweep). Rest = overlay, hover = the danger
        // tint (§8.1 state matrix: danger → red ink, hover red.1).
        background: 'overlay',
        color: 'red',
        ':hover': { background: 'red.1' },
        fontSize: 'Z',
        cursor: 'pointer',
        text: '{{ common.action.remove | polyglot }}',
        onClick: async (e, el, s) => {
          await el.scope.deleteInstance(el, s, s.slug, s.scope_type, s.scope_id)
        }
      }
    }
  },

  ConfigPreview: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const cfg = s.config
      return !cfg || Object.keys(cfg).length === 0
    },
    children: (el, s) => {
      const cfg = s.config || {}
      return Object.entries(cfg)
        .filter(([k]) => !k.includes('secret') && !k.includes('key') && !k.includes('token'))
        .slice(0, 4)
        .map(([k, v]) => ({ key: k, value: String(v) }))
    },
    childrenAs: 'state',
    childExtends: {
      flow: 'x',
      gap: 'Z',
      fontSize: 'Y2',
      Key: {
        color: 'caption',
        text: (el, s) => s.key || '',
        minWidth: '120px'
      },
      Value: {
        color: 'title',
        text: (el, s) => s.value || '—',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }
    }
  }
}
